import { CreateShapeCommand } from '../../../core/commands/CreateShapeCommand';
import type { ICommand } from '../../../core/commands/ICommand';
import type {
  CommandDeltaContribution,
  IScenarioRecordable,
} from '../../../core/data-profiles/scenarios/ScenarioRecordable';
import {
  createNodeId,
  type NodeAddition,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';

/**
 * CreateNetworkResourceCommand — creates an AWS / basic-network shape
 * and participates in scenario recording.
 *
 * Wraps the shared `CreateShapeCommand` with `category: 'network'` and
 * implements `IScenarioRecordable` so that when a user edits the
 * canvas while a scenario is active, CommandManager routes the add
 * through the ScenarioAwareCommand pipeline: the shape is staged as a
 * NodeAddition in the delta instead of mutating the live diagram.
 *
 * Outside scenario editing mode the wrapper is transparent — it runs
 * the inner `CreateShapeCommand` normally, so existing call sites
 * (import hydration, drop handler, shape picker) work unchanged.
 */
export interface CreateNetworkResourceParams {
  id?: string;
  type: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  text?: string;
  attrs?: Record<string, unknown>;
}

export class CreateNetworkResourceCommand implements ICommand, IScenarioRecordable {
  id: string;
  name = 'Create Network Resource';
  description?: string;
  timestamp: number;
  readonly isScenarioRecordable = true as const;

  private params: CreateNetworkResourceParams;
  private inner?: CreateShapeCommand;
  private scenarioNodeId: ReturnType<typeof createNodeId>;

  constructor(params: CreateNetworkResourceParams) {
    this.params = params;
    this.id = params.id ?? `create-network-resource-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.timestamp = Date.now();
    this.description = `Create network ${params.type}`;
    this.scenarioNodeId = createNodeId(params.id ?? `scenario-${this.id}`);
  }

  execute(): void {
    this.inner = new CreateShapeCommand({
      id: this.params.id,
      type: this.params.type,
      x: this.params.x,
      y: this.params.y,
      width: this.params.width,
      height: this.params.height,
      text: this.params.text,
      category: 'network',
      attrs: this.params.attrs,
    } as any);
    this.inner.execute();
  }

  undo(): void {
    this.inner?.undo?.();
  }

  redo(): void {
    this.inner?.redo?.();
  }

  getCreatedId(): string | undefined {
    return (this.inner as any)?.getCreatedId?.();
  }

  toScenarioDelta(): CommandDeltaContribution {
    const addition: NodeAddition = {
      id: this.scenarioNodeId,
      type: this.params.type,
      name: this.params.text ?? String((this.params.attrs as any)?.name ?? this.params.type),
      position: { x: this.params.x, y: this.params.y },
      props: { ...(this.params.attrs ?? {}) },
    };
    return { additions: [addition] };
  }

  getUndoDelta(): CommandDeltaContribution {
    return {
      removals: [
        {
          nodeId: this.scenarioNodeId,
          originalData: {
            name: this.params.text ?? this.params.type,
            type: this.params.type,
            props: { ...(this.params.attrs ?? {}) },
          },
        },
      ],
    };
  }
}
